import React, { useState, useMemo } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { Icon } from '../../atoms'
import { InfoTooltip } from '../../InfoTooltip'
import { TooltipMetricExplanation } from '../../TooltipMetricExplanation'
import { AnalysisTabButtonMenu } from '../../molecules'
import { AnalysisSectionHeader } from '../../AnalysisSectionHeader'

import { EnhancedSunlightData } from '@popety-io/types'
import ReactECharts from 'echarts-for-react'

// Styled Components
const Container = styled.div`
  background: ${({ theme }) => theme.colors.background.primary};
  min-height: 100vh;
`

const Grid = styled.div<{ $columns?: number }>`
  display: grid;
  grid-template-columns: ${({ $columns }) =>
    $columns
      ? `repeat(${$columns}, 1fr)`
      : 'repeat(auto-fit, minmax(300px, 1fr))'};
  gap: 24px;
  margin: ${({ theme }) => theme.spacing.large};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`

const Card = styled.div<{ $highlight?: boolean }>`
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid
    ${({ theme, $highlight }) =>
      $highlight ? theme.colors.primary : theme.colors.border.default};
  border-radius: 12px;
  padding: 24px;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }
`

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`

const CardTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const CardIcon = styled.div<{ $color?: string }>`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${({ $color }) => $color || 'rgba(0, 173, 230, 0.1)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary};
`

const MainValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 4px;
`

const SubValue = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
`

const MetricRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};

  &:last-child {
    border-bottom: none;
  }
`

const MetricLabel = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
  display: flex;
  align-items: center;
  gap: 4px;
`

const MetricValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`

const ChartCard = styled(Card)`
  grid-column: 1 / -1;
`

const SeasonCard = styled(Card)`
  text-align: center;
  cursor: pointer;
  border-width: 2px;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`

const SeasonIcon = styled.div<{ $season: string }>`
  font-size: 48px;
  margin-bottom: 16px;
`

const SeasonName = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 8px 0;
`

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: 4px;
  overflow: hidden;
  margin-top: 12px;
`

const ProgressFill = styled.div<{ $percentage: number; $color?: string }>`
  height: 100%;
  width: ${({ $percentage }) => $percentage}%;
  background: ${({ $color, theme }) => $color || theme.colors.primary};
  transition: width 0.3s ease;
`

const Badge = styled.span<{ $type?: 'success' | 'warning' | 'info' }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  background: ${({ theme, $type }) => {
    switch ($type) {
      case 'success':
        return 'rgba(0, 179, 65, 0.1)'
      case 'warning':
        return 'rgba(255, 149, 0, 0.1)'
      default:
        return theme.colors.primaryLight
    }
  }};
  color: ${({ theme, $type }) => {
    switch ($type) {
      case 'success':
        return theme.colors.success
      case 'warning':
        return theme.colors.warning
      default:
        return theme.colors.primary
    }
  }};
`

export interface SunlightTabProps {
  data: EnhancedSunlightData | null
  loading?: boolean
  error?: string | null
}

type TabType = 'overview' | 'yearly' | 'seasonal' | 'solar'

export const SunlightTab: React.FC<SunlightTabProps> = ({
  data,
  loading,
  error,
}) => {
  const { t } = useTranslation('sunlight')
  const [activeTab, setActiveTab] = useState<TabType>('overview')
  const [selectedSeason, setSelectedSeason] = useState<string>('summer')

  // Calculate yearly sunlight data for chart
  const yearlyChartOptions = useMemo(() => {
    if (!data) return null

    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ]

    // Calculate monthly daylight hours based on location
    const currentYear = new Date().getFullYear()
    const monthlyDaylight = months.map((_, index) => {
      const _daysInMonth = new Date(currentYear, index + 1, 0).getDate()
      const middleOfMonth = new Date(currentYear, index, 15)

      // Approximation based on latitude
      const lat = data.location.latitude
      const dayOfYear = Math.floor(
        (middleOfMonth.getTime() - new Date(currentYear, 0, 0).getTime()) /
          86400000
      )
      const P = Math.asin(
        0.39795 * Math.cos((0.98563 * (dayOfYear - 173) * Math.PI) / 180)
      )

      const sunrise =
        12 -
        (Math.acos(-Math.tan((lat * Math.PI) / 180) * Math.tan(P)) * 180) /
          Math.PI /
          15
      const sunset =
        12 +
        (Math.acos(-Math.tan((lat * Math.PI) / 180) * Math.tan(P)) * 180) /
          Math.PI /
          15

      return Math.max(0, Math.min(24, sunset - sunrise))
    })

    return {
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const value = params[0].value
          return `${params[0].name}: ${value.toFixed(1)} hours`
        },
      },
      grid: {
        left: '5%',
        right: '5%',
        bottom: '10%',
        top: '10%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: months,
        axisLabel: {
          color: '#666',
        },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: 24,
        axisLabel: {
          color: '#666',
          formatter: '{value}h',
        },
      },
      series: [
        {
          name: 'Daylight Hours',
          type: 'line',
          data: monthlyDaylight,
          smooth: true,
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(0, 173, 230, 0.3)' },
                { offset: 1, color: 'rgba(0, 173, 230, 0.05)' },
              ],
            },
          },
          lineStyle: {
            color: '#00ADE6',
            width: 2,
          },
          itemStyle: {
            color: '#00ADE6',
          },
        },
      ],
    }
  }, [data])

  if (loading) {
    return (
      <Container>
        <Card>
          <CardHeader>
            <CardTitle>{t('states.loading')}</CardTitle>
          </CardHeader>
        </Card>
      </Container>
    )
  }

  if (error || !data) {
    return (
      <Container>
        <Card>
          <CardHeader>
            <CardTitle>{t('states.error')}</CardTitle>
          </CardHeader>
          <SubValue>{error || t('states.noData')}</SubValue>
        </Card>
      </Container>
    )
  }

  const getSeasonEmoji = (season: string) => {
    switch (season) {
      case 'summer':
        return '☀️'
      case 'winter':
        return '❄️'
      case 'spring':
        return '🌸'
      case 'autumn':
        return '🍂'
      default:
        return '🌍'
    }
  }

  const renderOverview = () => (
    <>
      <Grid $columns={3}>
        {/* Today's Sunlight */}
        <Card $highlight>
          <CardHeader>
            <CardTitle>{t('overview.todaySunlight.title')}</CardTitle>
            <CardIcon $color="rgba(255, 149, 0, 0.1)">
              <Icon name="sun" size="medium" />
            </CardIcon>
          </CardHeader>
          <MainValue>{data.today.durations.daylight.toFixed(1)}h</MainValue>
          <SubValue>{t('common.ofDaylight')}</SubValue>
          <ProgressBar>
            <ProgressFill
              $percentage={(data.today.durations.daylight / 24) * 100}
              $color="#FF9500"
            />
          </ProgressBar>
          <MetricRow>
            <MetricLabel>{t('today.sunrise')}</MetricLabel>
            <MetricValue>{data.today.times.sunrise}</MetricValue>
          </MetricRow>
          <MetricRow>
            <MetricLabel>{t('common.solarNoon')}</MetricLabel>
            <MetricValue>{data.today.times.solarNoon}</MetricValue>
          </MetricRow>
          <MetricRow>
            <MetricLabel>{t('today.sunset')}</MetricLabel>
            <MetricValue>{data.today.times.sunset}</MetricValue>
          </MetricRow>
        </Card>

        {/* Annual Average */}
        <Card>
          <CardHeader>
            <CardTitle>{t('overview.annualAverage.title')}</CardTitle>
            <CardIcon>
              <Icon name="calendar" size="medium" />
            </CardIcon>
          </CardHeader>
          <MainValue>{data.annual.averageDaylight.toFixed(1)}h</MainValue>
          <SubValue>{t('overview.annualAverage.subtitle')}</SubValue>
          <MetricRow>
            <MetricLabel>{t('overview.annualAverage.totalAnnual')}</MetricLabel>
            <MetricValue>{data.annual.totalDaylightHours}h</MetricValue>
          </MetricRow>
          <MetricRow>
            <MetricLabel>{t('overview.annualAverage.longestDay')}</MetricLabel>
            <MetricValue>
              {data.annual.longestDay.hours.toFixed(1)}h
            </MetricValue>
          </MetricRow>
          <MetricRow>
            <MetricLabel>{t('overview.annualAverage.shortestDay')}</MetricLabel>
            <MetricValue>
              {data.annual.shortestDay.hours.toFixed(1)}h
            </MetricValue>
          </MetricRow>
        </Card>

        {/* Solar Potential */}
        <Card>
          <CardHeader>
            <CardTitle>{t('overview.solarEnergy.title')}</CardTitle>
            <CardIcon $color="rgba(0, 179, 65, 0.1)">
              <Icon name="zap" size="medium" />
            </CardIcon>
          </CardHeader>
          <MainValue>{data.solar.efficiency}%</MainValue>
          <SubValue>{t('overview.solarEnergy.efficiencyPotential')}</SubValue>
          <MetricRow>
            <MetricLabel>{t('overview.solarEnergy.peakHoursDay')}</MetricLabel>
            <MetricValue>{data.solar.dailyPeakHours}h</MetricValue>
          </MetricRow>
          <MetricRow>
            <MetricLabel>{t('overview.solarEnergy.optimalAngle')}</MetricLabel>
            <MetricValue>{data.solar.optimalPanelAngle}°</MetricValue>
          </MetricRow>
          <MetricRow>
            <MetricLabel>{t('overview.solarEnergy.yearlyAverage')}</MetricLabel>
            <MetricValue>{data.solar.yearlyTotal}h</MetricValue>
          </MetricRow>
        </Card>
      </Grid>

      {/* Property Benefits */}
      <Grid $columns={2}>
        <Card>
          <CardHeader>
            <CardTitle>{t('overview.lightQuality.morning.title')}</CardTitle>
            <Badge $type="info">
              {data.today.quality.goldenHour.morning.duration.toFixed(1)}h{' '}
              {t('common.goldenHour')}
            </Badge>
          </CardHeader>
          <MetricRow>
            <MetricLabel>{t('common.goldenHour')}</MetricLabel>
            <MetricValue>
              {data.today.quality.goldenHour.morning.start} -{' '}
              {data.today.quality.goldenHour.morning.end}
            </MetricValue>
          </MetricRow>
          <MetricRow>
            <MetricLabel>{t('common.blueHour')}</MetricLabel>
            <MetricValue>
              {data.today.quality.blueHour.morning.start} -{' '}
              {data.today.quality.blueHour.morning.end}
            </MetricValue>
          </MetricRow>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('overview.lightQuality.evening.title')}</CardTitle>
            <Badge $type="info">
              {data.today.quality.goldenHour.evening.duration.toFixed(1)}h{' '}
              {t('common.goldenHour')}
            </Badge>
          </CardHeader>
          <MetricRow>
            <MetricLabel>{t('common.goldenHour')}</MetricLabel>
            <MetricValue>
              {data.today.quality.goldenHour.evening.start} -{' '}
              {data.today.quality.goldenHour.evening.end}
            </MetricValue>
          </MetricRow>
          <MetricRow>
            <MetricLabel>{t('common.blueHour')}</MetricLabel>
            <MetricValue>
              {data.today.quality.blueHour.evening.start} -{' '}
              {data.today.quality.blueHour.evening.end}
            </MetricValue>
          </MetricRow>
        </Card>
      </Grid>
    </>
  )

  const renderYearlyAnalysis = () => (
    <>
      <ChartCard>
        <CardHeader>
          <CardTitle>{t('yearly.chartTitle')}</CardTitle>
          <Badge $type="info">
            {t('common.location')}: {data.location.latitude.toFixed(2)}°,{' '}
            {data.location.longitude.toFixed(2)}°
          </Badge>
        </CardHeader>
        {yearlyChartOptions && (
          <ReactECharts
            option={yearlyChartOptions}
            style={{ height: '400px', width: '100%' }}
            theme="light"
          />
        )}
      </ChartCard>

      <Grid $columns={3}>
        <Card>
          <CardHeader>
            <CardTitle>{t('yearly.variation.title')}</CardTitle>
          </CardHeader>
          <MetricRow>
            <MetricLabel>{t('yearly.variation.maxVariation')}</MetricLabel>
            <MetricValue>
              {(
                data.annual.longestDay.hours - data.annual.shortestDay.hours
              ).toFixed(1)}
              h
            </MetricValue>
          </MetricRow>
          <MetricRow>
            <MetricLabel>{t('yearly.variation.mostSunlight')}</MetricLabel>
            <MetricValue>{data.annual.longestDay.date}</MetricValue>
          </MetricRow>
          <MetricRow>
            <MetricLabel>{t('yearly.variation.leastSunlight')}</MetricLabel>
            <MetricValue>{data.annual.shortestDay.date}</MetricValue>
          </MetricRow>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('yearly.bestMonths.title')}</CardTitle>
          </CardHeader>
          <MetricRow>
            <MetricLabel>{t('yearly.bestMonths.peakSeason')}</MetricLabel>
            <MetricValue>{t('yearly.bestMonths.peakPeriod')}</MetricValue>
          </MetricRow>
          <MetricRow>
            <MetricLabel>{t('yearly.bestMonths.averagePeak')}</MetricLabel>
            <MetricValue>
              {data.seasons.solstices.summer.daylight.toFixed(1)}h/day
            </MetricValue>
          </MetricRow>
          <MetricRow>
            <MetricLabel>{t('yearly.bestMonths.qualityRating')}</MetricLabel>
            <MetricValue>
              <Badge $type="success">{t('yearly.bestMonths.excellent')}</Badge>
            </MetricValue>
          </MetricRow>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('yearly.challengingMonths.title')}</CardTitle>
          </CardHeader>
          <MetricRow>
            <MetricLabel>{t('yearly.challengingMonths.lowSeason')}</MetricLabel>
            <MetricValue>{t('yearly.challengingMonths.lowPeriod')}</MetricValue>
          </MetricRow>
          <MetricRow>
            <MetricLabel>
              {t('yearly.challengingMonths.averageLow')}
            </MetricLabel>
            <MetricValue>
              {data.seasons.solstices.winter.daylight.toFixed(1)}h/day
            </MetricValue>
          </MetricRow>
          <MetricRow>
            <MetricLabel>{t('yearly.bestMonths.qualityRating')}</MetricLabel>
            <MetricValue>
              <Badge $type="warning">
                {t('yearly.challengingMonths.limited')}
              </Badge>
            </MetricValue>
          </MetricRow>
        </Card>
      </Grid>
    </>
  )

  const renderSeasonalAnalysis = () => (
    <>
      <Grid $columns={4}>
        <SeasonCard
          onClick={() => setSelectedSeason('spring')}
          $highlight={selectedSeason === 'spring'}
        >
          <SeasonIcon $season="spring">{getSeasonEmoji('spring')}</SeasonIcon>
          <SeasonName>{t('seasons.spring.equinox')}</SeasonName>
          <SubValue>{data.seasons.equinoxes.spring.date}</SubValue>
          <MainValue style={{ fontSize: '24px' }}>
            {data.seasons.equinoxes.spring.daylight.toFixed(1)}h
          </MainValue>
          <SubValue>{t('common.daylight')}</SubValue>
        </SeasonCard>

        <SeasonCard
          onClick={() => setSelectedSeason('summer')}
          $highlight={selectedSeason === 'summer'}
        >
          <SeasonIcon $season="summer">{getSeasonEmoji('summer')}</SeasonIcon>
          <SeasonName>{t('seasons.summer.solstice')}</SeasonName>
          <SubValue>{data.seasons.solstices.summer.date}</SubValue>
          <MainValue style={{ fontSize: '24px' }}>
            {data.seasons.solstices.summer.daylight.toFixed(1)}h
          </MainValue>
          <SubValue>{t('common.daylight')}</SubValue>
        </SeasonCard>

        <SeasonCard
          onClick={() => setSelectedSeason('autumn')}
          $highlight={selectedSeason === 'autumn'}
        >
          <SeasonIcon $season="autumn">{getSeasonEmoji('autumn')}</SeasonIcon>
          <SeasonName>{t('seasons.autumn.equinox')}</SeasonName>
          <SubValue>{data.seasons.equinoxes.autumn.date}</SubValue>
          <MainValue style={{ fontSize: '24px' }}>
            {data.seasons.equinoxes.autumn.daylight.toFixed(1)}h
          </MainValue>
          <SubValue>{t('common.daylight')}</SubValue>
        </SeasonCard>

        <SeasonCard
          onClick={() => setSelectedSeason('winter')}
          $highlight={selectedSeason === 'winter'}
        >
          <SeasonIcon $season="winter">{getSeasonEmoji('winter')}</SeasonIcon>
          <SeasonName>{t('seasons.winter.solstice')}</SeasonName>
          <SubValue>{data.seasons.solstices.winter.date}</SubValue>
          <MainValue style={{ fontSize: '24px' }}>
            {data.seasons.solstices.winter.daylight.toFixed(1)}h
          </MainValue>
          <SubValue>{t('common.daylight')}</SubValue>
        </SeasonCard>
      </Grid>

      <Card>
        <CardHeader>
          <CardTitle>
            {t(`sunlight.seasons.${selectedSeason}.name`)}{' '}
            {t('seasons.analysis.title', { season: '' }).replace(
              '{{season}} ',
              ''
            )}
          </CardTitle>
        </CardHeader>
        <Grid $columns={3}>
          <div>
            <MetricRow>
              <MetricLabel>{t('seasons.analysis.sunriseTime')}</MetricLabel>
              <MetricValue>
                {selectedSeason === 'summer'
                  ? data.seasons.solstices.summer.sunrise
                  : selectedSeason === 'winter'
                    ? data.seasons.solstices.winter.sunrise
                    : selectedSeason === 'spring'
                      ? data.seasons.equinoxes.spring.sunrise
                      : data.seasons.equinoxes.autumn.sunrise}
              </MetricValue>
            </MetricRow>
            <MetricRow>
              <MetricLabel>{t('seasons.analysis.sunsetTime')}</MetricLabel>
              <MetricValue>
                {selectedSeason === 'summer'
                  ? data.seasons.solstices.summer.sunset
                  : selectedSeason === 'winter'
                    ? data.seasons.solstices.winter.sunset
                    : selectedSeason === 'spring'
                      ? data.seasons.equinoxes.spring.sunset
                      : data.seasons.equinoxes.autumn.sunset}
              </MetricValue>
            </MetricRow>
          </div>
          <div>
            <MetricRow>
              <MetricLabel>
                {t('seasons.analysis.daylightDuration')}
              </MetricLabel>
              <MetricValue>
                {selectedSeason === 'summer'
                  ? `${data.seasons.solstices.summer.daylight.toFixed(1)}h`
                  : selectedSeason === 'winter'
                    ? `${data.seasons.solstices.winter.daylight.toFixed(1)}h`
                    : selectedSeason === 'spring'
                      ? `${data.seasons.equinoxes.spring.daylight.toFixed(1)}h`
                      : `${data.seasons.equinoxes.autumn.daylight.toFixed(1)}h`}
              </MetricValue>
            </MetricRow>
            <MetricRow>
              <MetricLabel>{t('seasons.analysis.vsAnnualAverage')}</MetricLabel>
              <MetricValue>
                {selectedSeason === 'summer'
                  ? `+${(data.seasons.solstices.summer.daylight - data.annual.averageDaylight).toFixed(1)}h`
                  : selectedSeason === 'winter'
                    ? `${(data.seasons.solstices.winter.daylight - data.annual.averageDaylight).toFixed(1)}h`
                    : selectedSeason === 'spring'
                      ? `${(data.seasons.equinoxes.spring.daylight - data.annual.averageDaylight).toFixed(1)}h`
                      : `${(data.seasons.equinoxes.autumn.daylight - data.annual.averageDaylight).toFixed(1)}h`}
              </MetricValue>
            </MetricRow>
          </div>
          <div>
            <MetricRow>
              <MetricLabel>{t('seasons.analysis.propertyImpact')}</MetricLabel>
              <MetricValue>
                <Badge
                  $type={
                    selectedSeason === 'summer'
                      ? 'success'
                      : selectedSeason === 'winter'
                        ? 'warning'
                        : 'info'
                  }
                >
                  {selectedSeason === 'summer'
                    ? t('seasons.analysis.optimal')
                    : selectedSeason === 'winter'
                      ? t('seasons.analysis.limited')
                      : t('seasons.analysis.moderate')}
                </Badge>
              </MetricValue>
            </MetricRow>
            <MetricRow>
              <MetricLabel>
                {t('seasons.analysis.energyEfficiency')}
              </MetricLabel>
              <MetricValue>
                {selectedSeason === 'summer'
                  ? t('seasons.analysis.high')
                  : selectedSeason === 'winter'
                    ? t('seasons.analysis.low')
                    : t('seasons.analysis.medium')}
              </MetricValue>
            </MetricRow>
          </div>
        </Grid>
      </Card>
    </>
  )

  const renderSolarPotential = () => (
    <>
      <Grid $columns={2}>
        <Card>
          <CardHeader>
            <CardTitle>{t('solar.panelInstallation.title')}</CardTitle>
            <Badge $type="success">
              {t('solar.panelInstallation.highPotential')}
            </Badge>
          </CardHeader>
          <MetricRow>
            <MetricLabel>
              {t('solar.panelInstallation.optimalPanelAngle.label')}
              <InfoTooltip>
                <TooltipMetricExplanation
                  title={t(
                    'solar.panelInstallation.optimalPanelAngle.tooltip.title'
                  )}
                  shortDescription={t(
                    'solar.panelInstallation.optimalPanelAngle.tooltip.shortDescription'
                  )}
                  calculation={t(
                    'solar.panelInstallation.optimalPanelAngle.tooltip.calculation',
                    { latitude: data.location.latitude.toFixed(2) }
                  )}
                  importance={t(
                    'solar.panelInstallation.optimalPanelAngle.tooltip.importance'
                  )}
                  limitations={t(
                    'solar.panelInstallation.optimalPanelAngle.tooltip.limitations'
                  )}
                  source={t(
                    'solar.panelInstallation.optimalPanelAngle.tooltip.source'
                  )}
                />
              </InfoTooltip>
            </MetricLabel>
            <MetricValue>{data.solar.optimalPanelAngle}°</MetricValue>
          </MetricRow>
          <MetricRow>
            <MetricLabel>
              {t('solar.panelInstallation.peakSunHours.label')}
              <InfoTooltip>
                <TooltipMetricExplanation
                  title={t(
                    'solar.panelInstallation.peakSunHours.tooltip.title'
                  )}
                  shortDescription={t(
                    'solar.panelInstallation.peakSunHours.tooltip.shortDescription'
                  )}
                  calculation={t(
                    'solar.panelInstallation.peakSunHours.tooltip.calculation'
                  )}
                  importance={t(
                    'solar.panelInstallation.peakSunHours.tooltip.importance'
                  )}
                  limitations={t(
                    'solar.panelInstallation.peakSunHours.tooltip.limitations'
                  )}
                  source={t(
                    'solar.panelInstallation.peakSunHours.tooltip.source'
                  )}
                />
              </InfoTooltip>
            </MetricLabel>
            <MetricValue>{data.solar.dailyPeakHours}h</MetricValue>
          </MetricRow>
          <MetricRow>
            <MetricLabel>
              {t('solar.panelInstallation.monthlyAverage.label')}
              <InfoTooltip>
                <TooltipMetricExplanation
                  title={t(
                    'solar.panelInstallation.monthlyAverage.tooltip.title'
                  )}
                  shortDescription={t(
                    'solar.panelInstallation.monthlyAverage.tooltip.shortDescription'
                  )}
                  calculation={t(
                    'solar.panelInstallation.monthlyAverage.tooltip.calculation'
                  )}
                  importance={t(
                    'solar.panelInstallation.monthlyAverage.tooltip.importance'
                  )}
                  limitations={t(
                    'solar.panelInstallation.monthlyAverage.tooltip.limitations'
                  )}
                  source={t(
                    'solar.panelInstallation.monthlyAverage.tooltip.source'
                  )}
                />
              </InfoTooltip>
            </MetricLabel>
            <MetricValue>{data.solar.monthlyAverage}h</MetricValue>
          </MetricRow>
          <MetricRow>
            <MetricLabel>
              {t('solar.panelInstallation.yearlyTotal.label')}
              <InfoTooltip>
                <TooltipMetricExplanation
                  title={t('solar.panelInstallation.yearlyTotal.tooltip.title')}
                  shortDescription={t(
                    'solar.panelInstallation.yearlyTotal.tooltip.shortDescription'
                  )}
                  calculation={t(
                    'solar.panelInstallation.yearlyTotal.tooltip.calculation'
                  )}
                  importance={t(
                    'solar.panelInstallation.yearlyTotal.tooltip.importance'
                  )}
                  limitations={t(
                    'solar.panelInstallation.yearlyTotal.tooltip.limitations'
                  )}
                  source={t(
                    'solar.panelInstallation.yearlyTotal.tooltip.source'
                  )}
                />
              </InfoTooltip>
            </MetricLabel>
            <MetricValue>{data.solar.yearlyTotal}h</MetricValue>
          </MetricRow>
          <MetricRow>
            <MetricLabel>
              {t('solar.panelInstallation.efficiencyRating.label')}
              <InfoTooltip>
                <TooltipMetricExplanation
                  title={t(
                    'solar.panelInstallation.efficiencyRating.tooltip.title'
                  )}
                  shortDescription={t(
                    'solar.panelInstallation.efficiencyRating.tooltip.shortDescription'
                  )}
                  calculation={t(
                    'solar.panelInstallation.efficiencyRating.tooltip.calculation'
                  )}
                  importance={t(
                    'solar.panelInstallation.efficiencyRating.tooltip.importance'
                  )}
                  limitations={t(
                    'solar.panelInstallation.efficiencyRating.tooltip.limitations'
                  )}
                  source={t(
                    'solar.panelInstallation.efficiencyRating.tooltip.source'
                  )}
                />
              </InfoTooltip>
            </MetricLabel>
            <MetricValue>{data.solar.efficiency}%</MetricValue>
          </MetricRow>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('solar.energySavings.title')}</CardTitle>
            <Badge $type="info">{t('solar.energySavings.estimated')}</Badge>
          </CardHeader>
          <MetricRow>
            <MetricLabel>
              {t('solar.energySavings.annualProduction.label')}
              <InfoTooltip>
                <TooltipMetricExplanation
                  title={t(
                    'solar.energySavings.annualProduction.tooltip.title'
                  )}
                  shortDescription={t(
                    'solar.energySavings.annualProduction.tooltip.shortDescription'
                  )}
                  calculation={t(
                    'solar.energySavings.annualProduction.tooltip.calculation'
                  )}
                  importance={t(
                    'solar.energySavings.annualProduction.tooltip.importance'
                  )}
                  limitations={t(
                    'solar.energySavings.annualProduction.tooltip.limitations'
                  )}
                  source={t(
                    'solar.energySavings.annualProduction.tooltip.source'
                  )}
                />
              </InfoTooltip>
            </MetricLabel>
            <MetricValue>
              {Math.round(data.solar.yearlyTotal * 5 * 0.2)} kWh/kWp
            </MetricValue>
          </MetricRow>
          <MetricRow>
            <MetricLabel>
              {t('solar.energySavings.co2Reduction.label')}
              <InfoTooltip>
                <TooltipMetricExplanation
                  title={t('solar.energySavings.co2Reduction.tooltip.title')}
                  shortDescription={t(
                    'solar.energySavings.co2Reduction.tooltip.shortDescription'
                  )}
                  calculation={t(
                    'solar.energySavings.co2Reduction.tooltip.calculation'
                  )}
                  importance={t(
                    'solar.energySavings.co2Reduction.tooltip.importance'
                  )}
                  limitations={t(
                    'solar.energySavings.co2Reduction.tooltip.limitations'
                  )}
                  source={t('solar.energySavings.co2Reduction.tooltip.source')}
                />
              </InfoTooltip>
            </MetricLabel>
            <MetricValue>
              {Math.round((data.solar.yearlyTotal * 5 * 0.2 * 0.4) / 1000)}{' '}
              {t('solar.energySavings.co2Reduction.unit')}
            </MetricValue>
          </MetricRow>
          <MetricRow>
            <MetricLabel>
              {t('solar.energySavings.selfSufficiency.label')}
              <InfoTooltip>
                <TooltipMetricExplanation
                  title={t('solar.energySavings.selfSufficiency.tooltip.title')}
                  shortDescription={t(
                    'solar.energySavings.selfSufficiency.tooltip.shortDescription'
                  )}
                  calculation={t(
                    'solar.energySavings.selfSufficiency.tooltip.calculation'
                  )}
                  categories={[
                    {
                      label: t(
                        'solar.energySavings.selfSufficiency.tooltip.categories.consumptionPatterns.label'
                      ),
                      description: t(
                        'solar.energySavings.selfSufficiency.tooltip.categories.consumptionPatterns.description'
                      ),
                    },
                    {
                      label: t(
                        'solar.energySavings.selfSufficiency.tooltip.categories.batteryCapacity.label'
                      ),
                      description: t(
                        'solar.energySavings.selfSufficiency.tooltip.categories.batteryCapacity.description'
                      ),
                    },
                    {
                      label: t(
                        'solar.energySavings.selfSufficiency.tooltip.categories.seasonalVariations.label'
                      ),
                      description: t(
                        'solar.energySavings.selfSufficiency.tooltip.categories.seasonalVariations.description'
                      ),
                    },
                    {
                      label: t(
                        'solar.energySavings.selfSufficiency.tooltip.categories.heatPumpUsage.label'
                      ),
                      description: t(
                        'solar.energySavings.selfSufficiency.tooltip.categories.heatPumpUsage.description'
                      ),
                    },
                    {
                      label: t(
                        'solar.energySavings.selfSufficiency.tooltip.categories.electricVehicle.label'
                      ),
                      description: t(
                        'solar.energySavings.selfSufficiency.tooltip.categories.electricVehicle.description'
                      ),
                    },
                  ]}
                  importance={t(
                    'solar.energySavings.selfSufficiency.tooltip.importance'
                  )}
                  limitations={t(
                    'solar.energySavings.selfSufficiency.tooltip.limitations'
                  )}
                  source={t(
                    'solar.energySavings.selfSufficiency.tooltip.source'
                  )}
                />
              </InfoTooltip>
            </MetricLabel>
            <MetricValue>
              {t('solar.energySavings.selfSufficiency.value')}
            </MetricValue>
          </MetricRow>
          <MetricRow>
            <MetricLabel>
              {t('solar.energySavings.roiPeriod.label')}
              <InfoTooltip>
                <TooltipMetricExplanation
                  title={t('solar.energySavings.roiPeriod.tooltip.title')}
                  shortDescription={t(
                    'solar.energySavings.roiPeriod.tooltip.shortDescription'
                  )}
                  calculation={t(
                    'solar.energySavings.roiPeriod.tooltip.calculation'
                  )}
                  categories={[
                    {
                      label: t(
                        'solar.energySavings.roiPeriod.tooltip.categories.installationCosts.label'
                      ),
                      description: t(
                        'solar.energySavings.roiPeriod.tooltip.categories.installationCosts.description'
                      ),
                    },
                    {
                      label: t(
                        'solar.energySavings.roiPeriod.tooltip.categories.electricityPrices.label'
                      ),
                      description: t(
                        'solar.energySavings.roiPeriod.tooltip.categories.electricityPrices.description'
                      ),
                    },
                    {
                      label: t(
                        'solar.energySavings.roiPeriod.tooltip.categories.subsidies.label'
                      ),
                      description: t(
                        'solar.energySavings.roiPeriod.tooltip.categories.subsidies.description'
                      ),
                    },
                    {
                      label: t(
                        'solar.energySavings.roiPeriod.tooltip.categories.feedInTariffs.label'
                      ),
                      description: t(
                        'solar.energySavings.roiPeriod.tooltip.categories.feedInTariffs.description'
                      ),
                    },
                    {
                      label: t(
                        'solar.energySavings.roiPeriod.tooltip.categories.maintenanceCosts.label'
                      ),
                      description: t(
                        'solar.energySavings.roiPeriod.tooltip.categories.maintenanceCosts.description'
                      ),
                    },
                  ]}
                  importance={t(
                    'solar.energySavings.roiPeriod.tooltip.importance'
                  )}
                  limitations={t(
                    'solar.energySavings.roiPeriod.tooltip.limitations'
                  )}
                  source={t('solar.energySavings.roiPeriod.tooltip.source')}
                />
              </InfoTooltip>
            </MetricLabel>
            <MetricValue>
              {t('solar.energySavings.roiPeriod.value')}
            </MetricValue>
          </MetricRow>
          <MetricRow>
            <MetricLabel>
              {t('solar.energySavings.propertyValue.label')}
              <InfoTooltip>
                <TooltipMetricExplanation
                  title={t('solar.energySavings.propertyValue.tooltip.title')}
                  shortDescription={t(
                    'solar.energySavings.propertyValue.tooltip.shortDescription'
                  )}
                  calculation={t(
                    'solar.energySavings.propertyValue.tooltip.calculation'
                  )}
                  categories={[
                    {
                      label: t(
                        'solar.energySavings.propertyValue.tooltip.categories.locationFactors.label'
                      ),
                      description: t(
                        'solar.energySavings.propertyValue.tooltip.categories.locationFactors.description'
                      ),
                    },
                    {
                      label: t(
                        'solar.energySavings.propertyValue.tooltip.categories.buyerPreferences.label'
                      ),
                      description: t(
                        'solar.energySavings.propertyValue.tooltip.categories.buyerPreferences.description'
                      ),
                    },
                    {
                      label: t(
                        'solar.energySavings.propertyValue.tooltip.categories.systemCondition.label'
                      ),
                      description: t(
                        'solar.energySavings.propertyValue.tooltip.categories.systemCondition.description'
                      ),
                    },
                    {
                      label: t(
                        'solar.energySavings.propertyValue.tooltip.categories.marketTrends.label'
                      ),
                      description: t(
                        'solar.energySavings.propertyValue.tooltip.categories.marketTrends.description'
                      ),
                    },
                  ]}
                  importance={t(
                    'solar.energySavings.propertyValue.tooltip.importance'
                  )}
                  limitations={t(
                    'solar.energySavings.propertyValue.tooltip.limitations'
                  )}
                  source={t('solar.energySavings.propertyValue.tooltip.source')}
                />
              </InfoTooltip>
            </MetricLabel>
            <MetricValue>
              {t('solar.energySavings.propertyValue.value')}
            </MetricValue>
          </MetricRow>
        </Card>
      </Grid>

      <Card>
        <CardHeader>
          <CardTitle>{t('solar.monthlyGeneration.title')}</CardTitle>
        </CardHeader>
        <Grid $columns={4}>
          {['Q1', 'Q2', 'Q3', 'Q4'].map((quarter, index) => (
            <div key={quarter}>
              <MetricRow>
                <MetricLabel>
                  {t('solar.monthlyGeneration.quarterAverage', { quarter })}
                </MetricLabel>
                <MetricValue>
                  {Math.round(
                    data.solar.monthlyAverage *
                      (1 + (index === 1 || index === 2 ? 0.3 : -0.2))
                  )}
                  {t('solar.monthlyGeneration.perDay')}
                </MetricValue>
              </MetricRow>
              <ProgressBar>
                <ProgressFill
                  $percentage={
                    ((data.solar.monthlyAverage *
                      (1 + (index === 1 || index === 2 ? 0.3 : -0.2))) /
                      12) *
                    100
                  }
                  $color={index === 1 || index === 2 ? '#00B341' : '#FF9500'}
                />
              </ProgressBar>
            </div>
          ))}
        </Grid>
      </Card>
    </>
  )

  const tabs = useMemo(
    () => [
      { id: 'overview', label: t('tabs.overview'), icon: 'sun' },
      { id: 'yearly', label: t('tabs.yearly'), icon: 'calendar' },
      { id: 'seasonal', label: t('tabs.seasonal'), icon: 'cloud-sun' },
      { id: 'solar', label: t('tabs.solar'), icon: 'zap' },
    ],
    [t]
  )

  return (
    <Container>
      <AnalysisSectionHeader
        title={t('header.title')}
        subtitle={`${t('header.subtitle', { latitude: data.location.latitude.toFixed(4), longitude: data.location.longitude.toFixed(4) })}`}
      />

      <AnalysisTabButtonMenu
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={(tabId) => setActiveTab(tabId as TabType)}
      />

      {activeTab === 'overview' && renderOverview()}
      {activeTab === 'yearly' && renderYearlyAnalysis()}
      {activeTab === 'seasonal' && renderSeasonalAnalysis()}
      {activeTab === 'solar' && renderSolarPotential()}
    </Container>
  )
}
